import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ProductSizes } from "@/components/florist-dashboard/product-form/ProductSizes";
import { PRODUCT_CATEGORIES, PRODUCT_TAGS, type ProductFormData } from "@/types/product";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
// import { productSchema } from "@/types/product"; // Temporarily commented out
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useCallback, useEffect, useRef } from "react";

interface ProductFormProps {
  onSubmit: (data: ProductFormData) => Promise<void>;
  isLoading?: boolean;
  initialData?: Partial<ProductFormData>;
}

export function ProductForm({ onSubmit, isLoading = false, initialData }: ProductFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const mounted = useRef(false);

  const formMethods = useForm<ProductFormData>({
    // resolver: zodResolver(productSchema), // Temporarily commented out
    defaultValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
      category: initialData?.category || PRODUCT_CATEGORIES[0],
      active: initialData?.active ?? true,
      occasions: initialData?.occasions || [],
      sizes: initialData?.sizes || [{
        size_name: "Standard",
        price: 0,
        image_url: null,
        use_product_image: true
      }]
    },
    mode: "onChange"
  });

  const { handleSubmit, formState, watch, setValue, register } = formMethods;
  const { errors, isDirty } = formState;

  // Prevent form reset on mount
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
  }, []);

  const onFormSubmit = useCallback(async (data: ProductFormData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to save product. Please try again.');
    }
  }, [onSubmit]);

  // Watch for form changes and log them
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      console.log(`Form field changed: ${name}`, { value, type });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Form {...formMethods}>
      <form ref={formRef} onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        <FormField
          control={formMethods.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Product title" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formMethods.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Product description" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formMethods.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select 
                value={field.value} 
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <FormLabel>Occasions</FormLabel>
          <div className="flex flex-wrap gap-2 mt-2">
            {PRODUCT_TAGS.map((tag) => (
              <Badge
                key={tag}
                variant={formMethods.getValues("occasions")?.includes(tag) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => {
                  const currentOccasions = formMethods.getValues("occasions") || [];
                  const newOccasions = currentOccasions.includes(tag)
                    ? currentOccasions.filter((t) => t !== tag)
                    : [...currentOccasions, tag];
                  formMethods.setValue("occasions", newOccasions, { shouldValidate: true });
                }}
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>

        <FormField
          control={formMethods.control}
          name="sizes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Size Options</FormLabel>
              <FormControl>
                <ProductSizes
                  sizes={field.value}
                  onChange={(sizes) => {
                    field.onChange(sizes);
                    formMethods.trigger("sizes");
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formMethods.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between">
              <div className="space-y-0.5">
                <FormLabel>Active</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Make this product visible to customers
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isLoading || !isDirty} data-action={initialData?.id ? "save-product" : "create-product"}>
          {isLoading ? "Saving..." : initialData?.id ? "Save Changes" : "Create Product"}
        </Button>
      </form>
    </Form>
  );
}
